/**
 * Cumple Sprint 13 (S14 update) — Detalle de hallazgo con workflow.
 *
 * Muestra metadata, permite transicionar status, ajustar severidad,
 * asignar (S14: dropdown real de usuarios del tenant), fijar due_date y
 * documentar resolution. Sección de timeline con todos los eventos
 * persistidos en finding_events.
 *
 * Sprint Y — Modal "Cerrar hallazgo" (UX spec #2b):
 *   CTA visible solo cuando status === 'resolved'.
 *   Requiere evidencia de remediación para severity high/critical.
 *   PATCH /findings/:id { status: 'closed' } al confirmar.
 */
import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppShell } from '../components/AppShell.js';

import {
  type FindingClassification,
  type FindingEventRecord,
  type FindingEvidenceRecord,
  type FindingPhotoRecord,
  type FindingSeverity,
  type FindingStatus,
  type FindingSummary,
  type PatchFindingInput,
  findingsApi,
} from '../api/findings.js';
import { type AssignableUser, usersApi } from '../api/users.js';
import { Badge, Card, LoadingSkeleton, PageHeader } from '../components/ui.js';
import { Button } from '../components/ui/Button.js';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter,
  ModalClose,
} from '../components/ui/Modal.js';
import {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  useToast,
} from '../components/ui/Toast.js';

const STATUSES: { value: FindingStatus; label: string }[] = [
  { value: 'open', label: 'Abierto' },
  { value: 'in_progress', label: 'En progreso' },
  { value: 'resolved', label: 'Resuelto' },
  { value: 'closed', label: 'Cerrado' },
  { value: 'dismissed', label: 'Descartado' },
];
const SEVERITIES: FindingSeverity[] = ['low', 'medium', 'high', 'critical'];
const CLASSIFICATIONS: { value: FindingClassification; label: string; hint: string }[] = [
  {
    value: 'nc_major',
    label: 'NC Mayor',
    hint: 'Incumplimiento sistémico — bloquea certificación',
  },
  {
    value: 'nc_minor',
    label: 'NC Menor',
    hint: 'Desviación puntual sin impacto sistémico',
  },
  {
    value: 'observation',
    label: 'Observación',
    hint: 'Información que no llega a no-conformidad',
  },
  { value: 'ofi', label: 'OFI', hint: 'Oportunidad de mejora' },
];

export function FindingDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBackTo, setBreadcrumb } = useAppShell();
  const [finding, setFinding] = useState<FindingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState<FindingStatus>('open');
  const [severity, setSeverity] = useState<FindingSeverity>('medium');
  const [classification, setClassification] = useState<FindingClassification>('nc_minor');
  const [resolution, setResolution] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [events, setEvents] = useState<FindingEventRecord[]>([]);
  const [photos, setPhotos] = useState<FindingPhotoRecord[]>([]);
  const [includeWholeInstance, setIncludeWholeInstance] = useState(false);
  const [evidence, setEvidence] = useState<FindingEvidenceRecord[]>([]);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [evidenceCaption, setEvidenceCaption] = useState('');

  // Sprint Y — Modal "Cerrar hallazgo"
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeNote, setCloseNote] = useState('');
  const [closing, setClosing] = useState(false);
  const [closeModalError, setCloseModalError] = useState<string | null>(null);
  const { toasts, toast, dismiss } = useToast();

  // Back button + breadcrumb (BB-UX-01 + BB-UX-03)
  useEffect(() => {
    setBackTo({ href: '/findings', label: 'Hallazgos' });
    return () => setBackTo(null);
  }, [setBackTo]);

  useEffect(() => {
    const title = finding?.question_ref ?? id ?? '';
    setBreadcrumb([{ label: 'Hallazgos', href: '/findings' }, { label: title }]);
    return () => setBreadcrumb([]);
  }, [setBreadcrumb, finding, id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      findingsApi.get(id),
      usersApi.assignable(),
      findingsApi.events(id),
      findingsApi.photos(id),
      findingsApi.listEvidence(id),
    ])
      .then(([f, u, ev, ph, ed]) => {
        if (cancelled) return;
        setFinding(f.finding);
        setStatus(f.finding.status);
        setSeverity(f.finding.severity);
        setClassification(f.finding.classification);
        setResolution(f.finding.resolution ?? '');
        setDueDate(f.finding.due_date?.slice(0, 10) ?? '');
        setAssignedTo(f.finding.assigned_to_user_id ?? '');
        setUsers(u.users);
        setEvents(ev.events);
        setPhotos(ph.photos);
        setEvidence(ed.evidence);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error cargando finding');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!id || !finding) return;
    setSaving(true);
    setError(null);
    const patch: PatchFindingInput = {};
    if (status !== finding.status) patch.status = status;
    if (severity !== finding.severity) patch.severity = severity;
    if (classification !== finding.classification) patch.classification = classification;
    if (resolution !== (finding.resolution ?? '')) patch.resolution = resolution || null;
    const newDueIso = dueDate ? new Date(dueDate + 'T00:00:00Z').toISOString() : null;
    if (newDueIso !== finding.due_date) patch.due_date = newDueIso;
    const newAssignee = assignedTo === '' ? null : assignedTo;
    if (newAssignee !== finding.assigned_to_user_id) patch.assigned_to_user_id = newAssignee;
    try {
      const updated = await findingsApi.patch(id, patch);
      setFinding(updated.finding);
      // refrescamos timeline para reflejar los eventos recién creados
      const ev = await findingsApi.events(id);
      setEvents(ev.events);
    } catch (err) {
      // S15-D · Traducir el bloqueo de evidencia a mensaje claro.
      const msg = err instanceof Error ? err.message : 'Error guardando';
      if (msg.includes('remediation_evidence_required')) {
        setError(
          'Antes de marcar este hallazgo como resuelto o cerrado debes subir al menos una foto de evidencia que demuestre el control aplicado. Severidad ' +
            (finding?.severity ?? '') +
            ' lo requiere.',
        );
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleEvidenceUpload(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setEvidenceUploading(true);
    setError(null);
    try {
      const created = await findingsApi.uploadEvidence(
        id,
        file,
        evidenceCaption.trim() || undefined,
      );
      setEvidence((prev) => [created, ...prev]);
      setEvidenceCaption('');
      // refrescar timeline (evento evidence_uploaded)
      const ev = await findingsApi.events(id);
      setEvents(ev.events);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error subiendo evidencia');
    } finally {
      setEvidenceUploading(false);
      e.target.value = '';
    }
  }

  async function handleEvidenceDelete(evidenceId: string): Promise<void> {
    if (!id) return;
    if (!confirm('¿Eliminar esta evidencia? La acción queda en el histórico.')) return;
    try {
      await findingsApi.deleteEvidence(evidenceId);
      setEvidence((prev) => prev.filter((e) => e.id !== evidenceId));
      const ev = await findingsApi.events(id);
      setEvents(ev.events);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error eliminando');
    }
  }

  async function handleCloseFinding(): Promise<void> {
    if (!id || !finding) return;
    setClosing(true);
    setCloseModalError(null);
    const patch: PatchFindingInput = { status: 'closed' };
    // Si hay nota de cierre, actualiza la resolución
    if (closeNote.trim()) patch.resolution = closeNote.trim();
    try {
      const updated = await findingsApi.patch(id, patch);
      setFinding(updated.finding);
      setStatus(updated.finding.status);
      // Refrescar timeline para reflejar el evento de cierre
      const ev = await findingsApi.events(id);
      setEvents(ev.events);
      setCloseModalOpen(false);
      setCloseNote('');
      toast({ title: 'Hallazgo cerrado correctamente.', duration: 3000 });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error cerrando hallazgo';
      setCloseModalError(msg);
    } finally {
      setClosing(false);
    }
  }

  function userEmail(uid: string | null): string {
    if (!uid) return '—';
    const u = users.find((x) => x.id === uid);
    return u ? u.email : `${uid.slice(0, 8)}…`;
  }

  function eventLabel(ev: FindingEventRecord): string {
    switch (ev.kind) {
      case 'created':
        return `Hallazgo creado (severidad inicial: ${ev.to_value ?? '—'})`;
      case 'status_changed':
        return `Status: ${ev.from_value ?? '—'} → ${ev.to_value ?? '—'}`;
      case 'severity_changed':
        return `Severidad: ${ev.from_value ?? '—'} → ${ev.to_value ?? '—'}`;
      case 'assigned':
        return (
          `Asignado a ${userEmail(ev.to_value)}` +
          (ev.from_value ? ` (antes: ${userEmail(ev.from_value)})` : '')
        );
      case 'due_date_changed':
        return `Fecha límite: ${ev.from_value?.slice(0, 10) ?? 'sin fecha'} → ${ev.to_value?.slice(0, 10) ?? 'sin fecha'}`;
      case 'resolution_updated':
        return ev.to_value
          ? `Resolución actualizada: «${ev.to_value.slice(0, 80)}${ev.to_value.length > 80 ? '…' : ''}»`
          : 'Resolución borrada';
      case 'evidence_uploaded':
        return 'Evidencia de remediación subida';
      case 'evidence_deleted':
        return 'Evidencia de remediación eliminada';
      case 'note_snapshot_at_capture':
        return ev.to_value
          ? `Nota del auditor capturada: «${ev.to_value.slice(0, 80)}${ev.to_value.length > 80 ? '…' : ''}»`
          : 'Nota del auditor capturada';
      case 'classification_changed':
        return `Clasificación ISO: ${ev.from_value ?? '—'} → ${ev.to_value ?? '—'}`;
    }
  }

  if (loading) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-8 space-y-6">
        <LoadingSkeleton rows={3} />
      </main>
    );
  }

  if (!finding) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-8 space-y-6">
        <PageHeader eyebrow="HALLAZGO" title="No encontrado" />
        {error && (
          <div
            role="alert"
            className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700"
          >
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={() => navigate('/findings')}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          ← Volver a la lista
        </button>
      </main>
    );
  }

  // Lógica de habilitación del botón de cierre:
  // Para severity high/critical se requiere al menos 1 evidencia de remediación.
  const evidenceRequired =
    (finding.severity === 'high' || finding.severity === 'critical') && evidence.length === 0;
  const canSubmitClose = !evidenceRequired;

  return (
    <ToastProvider>
      <main className="mx-auto max-w-3xl px-6 py-8 space-y-6">
        {/* Modal "Cerrar hallazgo" — Sprint Y UX spec #2b */}
        <Modal
          open={closeModalOpen}
          onOpenChange={(open) => {
            if (!closing) setCloseModalOpen(open);
          }}
        >
          <ModalContent size="sm">
            <ModalHeader>
              <ModalTitle>Confirmar cierre del hallazgo</ModalTitle>
              <ModalDescription>
                Al cerrar, el hallazgo queda bloqueado para edición de workflow.
              </ModalDescription>
            </ModalHeader>

            <div className="space-y-4 py-2">
              {/* Nota de cierre opcional */}
              <div>
                <label
                  htmlFor="close_note"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Nota de cierre (opcional)
                </label>
                <textarea
                  id="close_note"
                  rows={3}
                  value={closeNote}
                  onChange={(e) => setCloseNote(e.target.value)}
                  placeholder="Ej. Control implementado el 2026-05-28, referencia expediente #004."
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
              </div>

              {/* Estado de evidencia de remediación */}
              {evidence.length > 0 ? (
                <p className="text-sm text-emerald-700">
                  ✓ Evidencia de remediación registrada ({evidence.length} archivo
                  {evidence.length === 1 ? '' : 's'})
                </p>
              ) : finding.severity === 'high' || finding.severity === 'critical' ? (
                // Advertencia obligatoria para high/critical sin evidencia — WCAG 4.1.3
                <p role="alert" className="text-sm text-rose-700 font-medium">
                  Debes subir al menos una foto antes de cerrar (severidad {finding.severity}).
                </p>
              ) : null}

              {/* Error inline del submit — WCAG 4.1.3 */}
              {closeModalError && (
                <p role="alert" className="text-sm text-rose-700">
                  {closeModalError}
                </p>
              )}
            </div>

            <ModalFooter>
              <ModalClose asChild>
                <Button variant="secondary" disabled={closing}>
                  Cancelar
                </Button>
              </ModalClose>
              <Button
                variant="primary"
                disabled={!canSubmitClose || closing}
                aria-busy={closing}
                onClick={() => void handleCloseFinding()}
              >
                {closing ? 'Cerrando…' : 'Cerrar hallazgo'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <PageHeader
          eyebrow="HALLAZGO"
          title={finding.question_text}
          subtitle={`${finding.question_ref} · respuesta capturada: «${finding.response_value}»`}
        />

        {error && (
          <div
            role="alert"
            className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700"
          >
            {error}
          </div>
        )}

        <Card title="Workflow" hint="Cambia el estado y documenta la resolución">
          <form onSubmit={(e) => void submit(e)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="status" className="block text-xs font-semibold text-slate-700 mb-1">
                  Estado
                </label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as FindingStatus)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="severity"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Severidad
                </label>
                <select
                  id="severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as FindingSeverity)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                >
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {s.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label
                  htmlFor="classification"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Clasificación ISO 19011 §6.4.8
                </label>
                <select
                  id="classification"
                  value={classification}
                  onChange={(e) => setClassification(e.target.value as FindingClassification)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                >
                  {CLASSIFICATIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label} — {c.hint}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-400">
                  Solo NC mayor/menor cuentan en "hallazgos abiertos" del dashboard. Observación/OFI
                  son trackable pero no bloquean.
                </p>
              </div>
              <div>
                <label
                  htmlFor="assigned_to"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Asignado a
                </label>
                <select
                  id="assigned_to"
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                >
                  <option value="">Sin asignar</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.email} {u.role === 'admin' ? '· admin' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="due_date"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Fecha límite (opcional)
                </label>
                <input
                  id="due_date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
              </div>
              <div className="md:col-span-2">
                <label
                  htmlFor="resolution"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Resolución / nota
                </label>
                <textarea
                  id="resolution"
                  rows={4}
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  placeholder="Ej. Política aprobada el 2026-05-15, link al expediente …"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {/* CTA "Cerrar hallazgo" — visible solo cuando status === 'resolved' (UX spec #2b) */}
                {finding.status === 'resolved' && (
                  <Button
                    variant="primary"
                    size="sm"
                    type="button"
                    onClick={() => {
                      setCloseNote('');
                      setCloseModalError(null);
                      setCloseModalOpen(true);
                    }}
                  >
                    Cerrar hallazgo
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/findings')}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  ← Volver
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-brand-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          </form>
        </Card>

        {finding.auditor_note && (
          <Card
            title="Nota del auditor al levantar"
            hint="Snapshot inmutable capturado en la auditoría (no editable)"
          >
            <blockquote className="border-l-4 border-brand-500 bg-brand-50 px-4 py-3 text-sm text-brand-900 italic">
              {finding.auditor_note}
            </blockquote>
          </Card>
        )}

        <Card title="Trazabilidad" hint="De dónde viene este hallazgo">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Assessment
              </dt>
              <dd className="font-mono text-xs text-slate-700 break-all">
                {finding.assessment_id}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Survey instance
              </dt>
              <dd className="font-mono text-xs text-slate-700 break-all">
                {finding.survey_instance_id}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Creado
              </dt>
              <dd className="text-slate-700">
                {new Date(finding.created_at).toLocaleString('es-DO')}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Última actualización
              </dt>
              <dd className="text-slate-700">
                {new Date(finding.updated_at).toLocaleString('es-DO')}
              </dd>
            </div>
            {finding.closed_at && (
              <div className="md:col-span-2">
                <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Cerrado el
                </dt>
                <dd className="text-slate-700">
                  {new Date(finding.closed_at).toLocaleString('es-DO')} ·{' '}
                  <Badge tone="emerald">Workflow cerrado</Badge>
                </dd>
              </div>
            )}
          </dl>
        </Card>

        <Card
          title="Evidencia"
          hint={
            photos.length === 0
              ? 'Sin fotos asociadas a esta pregunta'
              : `${photos.length} foto${photos.length === 1 ? '' : 's'} de ${includeWholeInstance ? 'todo el survey instance' : 'la pregunta'}`
          }
          actions={
            <button
              type="button"
              onClick={() => {
                const next = !includeWholeInstance;
                setIncludeWholeInstance(next);
                if (id) {
                  findingsApi
                    .photos(id, { includeWholeInstance: next })
                    .then((d) => setPhotos(d.photos))
                    .catch(() => undefined);
                }
              }}
              className="text-xs font-semibold text-brand-700 hover:text-brand-800"
            >
              {includeWholeInstance ? '← Solo esta pregunta' : 'Ver todo el instance →'}
            </button>
          }
        >
          {photos.length === 0 ? (
            <p className="text-sm text-slate-500">
              No hay fotos capturadas para <span className="font-mono">{finding.question_ref}</span>
              . Si la auditoría aún está en curso, el auditor puede subir evidencia desde la
              pantalla de captura.
            </p>
          ) : (
            <ul className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {photos.map((p) => (
                <li key={p.id} className="relative group">
                  <a href={p.file_url} target="_blank" rel="noreferrer" className="block">
                    <img
                      src={p.file_url}
                      alt={`Evidencia ${p.question_ref}`}
                      className="w-full aspect-square object-cover rounded-lg border border-slate-200 group-hover:opacity-90"
                      loading="lazy"
                    />
                    <p className="mt-1 text-xs font-mono text-slate-500 truncate">
                      {p.question_ref}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {new Date(p.uploaded_at).toLocaleString('es-DO')} ·{' '}
                      {(p.size_bytes / 1024).toFixed(0)} KB
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title="Evidencia de remediación"
          hint={
            finding.severity === 'high' || finding.severity === 'critical'
              ? 'OBLIGATORIA para cerrar este hallazgo (severidad ' + finding.severity + ')'
              : 'Sube foto del control aplicado una vez resuelto el hallazgo'
          }
        >
          <div className="space-y-3">
            {evidence.length > 0 ? (
              <ul className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {evidence.map((e) => (
                  <li key={e.id} className="relative group">
                    <a href={e.file_url} target="_blank" rel="noreferrer" className="block">
                      <img
                        src={e.file_url}
                        alt={e.caption ?? 'Evidencia de remediación'}
                        className="w-full aspect-square object-cover rounded-lg border-2 border-emerald-300 group-hover:opacity-90"
                        loading="lazy"
                      />
                      {e.caption && (
                        <p className="mt-1 text-xs text-slate-700 line-clamp-2">{e.caption}</p>
                      )}
                      <p className="text-[10px] text-slate-400">
                        {new Date(e.uploaded_at).toLocaleString('es-DO')} ·{' '}
                        {(e.size_bytes / 1024).toFixed(0)} KB
                      </p>
                    </a>
                    <button
                      type="button"
                      onClick={() => void handleEvidenceDelete(e.id)}
                      aria-label="Eliminar evidencia"
                      className="absolute top-1 right-1 rounded-full bg-white/90 hover:bg-rose-50 text-rose-600 hover:text-rose-700 text-xs px-2 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity border border-slate-200"
                    >
                      Eliminar
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-slate-300 px-4 py-6 text-center">
                <p className="text-sm text-slate-500">
                  Aún no se ha subido evidencia del control aplicado.
                </p>
                {(finding.severity === 'high' || finding.severity === 'critical') && (
                  <p className="mt-1 text-xs text-rose-600 font-semibold">
                    Severidad {finding.severity}: se requiere al menos una foto antes de marcar como
                    resuelto o cerrado.
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label
                  htmlFor="evidence_caption"
                  className="block text-xs font-semibold text-slate-700 mb-1"
                >
                  Caption / descripción de la evidencia (opcional)
                </label>
                <input
                  id="evidence_caption"
                  type="text"
                  value={evidenceCaption}
                  onChange={(e) => setEvidenceCaption(e.target.value)}
                  placeholder="Ej. Política firmada por dirección, configuración del CCTV…"
                  disabled={evidenceUploading}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <label
                className={
                  'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors cursor-pointer ' +
                  (evidenceUploading
                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    : 'bg-brand-700 text-white hover:bg-brand-700/90')
                }
              >
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  onChange={(e) => void handleEvidenceUpload(e)}
                  disabled={evidenceUploading}
                  className="hidden"
                />
                {evidenceUploading ? 'Subiendo…' : '+ Subir evidencia'}
              </label>
            </div>
          </div>
        </Card>

        <Card title="Línea de tiempo" hint="Histórico de transiciones del workflow">
          {events.length === 0 ? (
            <p className="text-sm text-slate-500">Sin eventos aún.</p>
          ) : (
            <ol className="relative border-l border-slate-200 pl-4 space-y-3">
              {events.map((ev) => (
                <li key={ev.id} className="text-sm text-slate-700">
                  <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-brand-700" />
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-xs font-mono text-slate-400">
                      {new Date(ev.ts).toLocaleString('es-DO')}
                    </span>
                    <span className="text-xs text-slate-500">
                      {ev.actor_user_id ? `· ${userEmail(ev.actor_user_id)}` : '· sistema'}
                    </span>
                  </div>
                  <p className="mt-0.5">{eventLabel(ev)}</p>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </main>

      {/* Toasts — patrón idéntico a PortalTokensPanel */}
      {toasts.map((t) => (
        <Toast
          key={t.id}
          open={t.open}
          onOpenChange={(open) => {
            if (!open) dismiss(t.id);
          }}
        >
          <ToastTitle>{t.title}</ToastTitle>
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
